import React, { useEffect, useRef, useState } from "react";
import { isDefined, isSSR } from "./../../wix-rich-content-common/index.js";
import IframePlayer from "../IframePlayer.js";
import { ReactPlayerWrapper } from "../reactPlayerWrapper.js";
import styles from "../statics/styles/video-viewer-scss.js";
const Video = ({
  config,
  componentData,
  nodeId,
  isLight,
  onReady,
  isLoaded,
  seoMode,
  muted,
  onDuration,
  disabled,
  onUpdateProcessing
}) => {
  const retryTimeoutRef = useRef(null);
  const [playerKey, setPlayerKey] = useState("");
  const [state, setState] = useState(() => {
    const { src } = componentData.video || {};
    const getInitialVideoUrlState = () => {
      if (src?.url) {
        const normalizedUrl = normalizeVideoUrl(src.url);
        return normalizedUrl ? { type: "READY", loadedUrl: normalizedUrl } : { type: "INITIAL", loadedUrl: void 0 };
      }
      if (src?.id && config.getVideoUrl) {
        try {
          const url = config.getVideoUrl({ pathname: src.id });
          if (typeof url === "string") {
            const normalizedUrl = normalizeVideoUrl(url);
            return normalizedUrl ? { type: "READY", loadedUrl: normalizedUrl } : { type: "INITIAL", loadedUrl: void 0 };
          }
        } catch {
        }
      }
      return { type: "INITIAL", loadedUrl: void 0 };
    };
    const getInitialThumbnailUrlState = () => {
      const thumbnailSrc = componentData?.thumbnail?.src?.url;
      return thumbnailSrc ? { type: "READY", loadedUrl: thumbnailSrc } : { type: "INITIAL", loadedUrl: void 0 };
    };
    return {
      videoUrl: getInitialVideoUrlState(),
      thumbnailUrl: getInitialThumbnailUrlState()
    };
  });
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    setPlayerKey("mounted");
  }, []);
  const scheduleNextRetry = (currentAttempt, _videoUrl) => {
    const maxRetries = 20;
    const nextAttempt = currentAttempt + 1;
    if (nextAttempt > maxRetries) {
      setState((prev) => {
        if (prev.videoUrl.type === "PROCESSING") {
          onUpdateProcessing?.(false);
          return {
            ...prev,
            videoUrl: {
              type: "ERROR",
              error: "Max retries exceeded",
              retryCount: nextAttempt,
              loadedUrl: prev.videoUrl.loadedUrl
            }
          };
        }
        return prev;
      });
      return;
    }
    setState((prev) => {
      if (prev.videoUrl.type === "PROCESSING") {
        return {
          ...prev,
          videoUrl: {
            ...prev.videoUrl,
            retryCount: nextAttempt
          }
        };
      }
      return prev;
    });
    const delay = Math.min(1e3 * Math.pow(2, nextAttempt - 1), 3e4);
    retryTimeoutRef.current = setTimeout(() => {
      setPlayerKey(`retry-${nextAttempt}-${Date.now()}`);
    }, delay);
  };
  const retryVideoUrl = () => {
    setState((prevState) => {
      if (prevState.videoUrl.type === "PROCESSING" || prevState.videoUrl.type === "INITIAL" || prevState.videoUrl.type === "LOADING") {
        return prevState;
      }
      const loadedUrl = prevState.videoUrl.loadedUrl;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      onUpdateProcessing?.(true);
      scheduleNextRetry(0, loadedUrl);
      return {
        ...prevState,
        videoUrl: {
          type: "PROCESSING",
          retryCount: 1,
          loadedUrl
        }
      };
    });
  };
  useEffect(() => {
    const { src } = componentData.video || {};
    if (state.videoUrl.type === "LOADING" || state.videoUrl.type === "PROCESSING") return;
    if (src?.url) {
      const normalizedUrl = normalizeVideoUrl(src.url);
      const currentUrl = state.videoUrl.loadedUrl;
      if (normalizedUrl && normalizedUrl !== currentUrl) {
        setState((prev) => ({
          ...prev,
          videoUrl: { type: "READY", loadedUrl: normalizedUrl }
        }));
      }
      return;
    }
    if (src?.id && config.getVideoUrl) {
      const loadVideo = async () => {
        if (!src.id) return;
        setState((prev) => ({
          ...prev,
          videoUrl: { type: "LOADING", loadedUrl: void 0 }
        }));
        try {
          const url = await config.getVideoUrl({ pathname: src.id });
          if (url) {
            const normalizedUrl = normalizeVideoUrl(url);
            if (normalizedUrl) {
              setState((prev) => {
                if (prev.videoUrl.type === "LOADING") {
                  return {
                    ...prev,
                    videoUrl: { type: "READY", loadedUrl: normalizedUrl }
                  };
                }
                return prev;
              });
              return;
            }
          }
        } catch {
          retryVideoUrl();
        }
      };
      loadVideo();
    }
  }, [componentData.video?.src, config.getVideoUrl]);
  useEffect(() => {
    const { src } = componentData.thumbnail || {};
    const thumbnailId = src?.id;
    const thumbnailSrc = src?.url;
    const currentThumbnailUrl = state.thumbnailUrl.loadedUrl;
    if (thumbnailSrc && thumbnailSrc !== currentThumbnailUrl) {
      setState((prev) => ({
        ...prev,
        thumbnailUrl: { type: "READY", loadedUrl: thumbnailSrc }
      }));
      return;
    }
    if (thumbnailId && config.getThumbnailUrl && !currentThumbnailUrl) {
      const loadThumbnail = async () => {
        setState((prev) => ({
          ...prev,
          thumbnailUrl: { type: "LOADING", loadedUrl: void 0 }
        }));
        try {
          const url = await config.getThumbnailUrl({ pathname: thumbnailId });
          if (url) {
            setState((prev) => ({
              ...prev,
              thumbnailUrl: { type: "READY", loadedUrl: url }
            }));
          }
        } catch {
          setState((prev) => ({
            ...prev,
            thumbnailUrl: {
              type: "ERROR",
              error: "Failed to load thumbnail",
              retryCount: 0,
              loadedUrl: ""
            }
          }));
        }
      };
      loadThumbnail();
    }
  }, [componentData.thumbnail?.src, config.getThumbnailUrl]);
  const onReactPlayerReady = (player) => {
    setState((prev) => {
      if (prev.videoUrl.type === "PROCESSING") {
        onUpdateProcessing?.(false);
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }
        return {
          ...prev,
          videoUrl: {
            type: "READY",
            loadedUrl: prev.videoUrl.loadedUrl
          }
        };
      }
      if (prev.videoUrl.type === "ERROR") {
        onUpdateProcessing?.(false);
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }
        return {
          ...prev,
          videoUrl: {
            type: "READY",
            loadedUrl: prev.videoUrl.loadedUrl
          }
        };
      }
      const currentVideoUrl = prev.videoUrl.loadedUrl;
      if (!currentVideoUrl) {
        return prev;
      }
      return {
        ...prev,
        videoUrl: {
          type: "READY",
          loadedUrl: currentVideoUrl
        }
      };
    });
    if (onReady) {
      onReady(player);
      return;
    }
  };
  const onVideoError = () => {
    const { src } = componentData.video || {};
    const isCustomVideo2 = !!src?.id;
    if (!isCustomVideo2 || !src?.id) {
      return;
    }
    if (state.videoUrl.type === "READY") {
      return retryVideoUrl();
    }
    if (state.videoUrl.type === "PROCESSING") {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return scheduleNextRetry(state.videoUrl.retryCount, state.videoUrl.loadedUrl);
    }
  };
  const handleContextMenu = (e) => componentData.disableDownload && e.preventDefault();
  const getDownloadProps = () => {
    if (isDefined(componentData?.disableDownload) && componentData.disableDownload || isDefined(config?.disableDownload) && config.disableDownload) {
      return {
        config: {
          file: {
            attributes: {
              controlsList: "nodownload"
            }
          }
        }
      };
    }
    return {
      config: {}
    };
  };
  const getLightProps = () => {
    if (!isLight) {
      return {
        isLight: false
      };
    }
    return {
      isLight: true,
      thumbnail: state.thumbnailUrl.loadedUrl
    };
  };
  const videoUrl = state.videoUrl.loadedUrl;
  if (!videoUrl) return null;
  const isCustomVideo = !!componentData.video?.src?.id;
  const shouldRenderIframe = isSSR() && seoMode;
  const { autoPlay = false, playInLoop = false, showControls = true } = componentData.options || {};
  const poster = state.videoUrl.type === "PROCESSING" ? state.thumbnailUrl.loadedUrl : void 0;
  return shouldRenderIframe ? /* @__PURE__ */ React.createElement(IframePlayer, { isCustomVideo, url: videoUrl }) : /* @__PURE__ */ React.createElement(
    ReactPlayerWrapper,
    {
      className: styles.video_player,
      onContextMenu: handleContextMenu,
      controls: isLoaded !== false && showControls,
      url: videoUrl,
      onReady: onReactPlayerReady,
      onError: onVideoError,
      disabled,
      width: "100%",
      height: "100%",
      key: playerKey,
      onDuration,
      onProgress: (progress) => {
        config.onProgress?.({ ...progress, id: nodeId });
      },
      progressInterval: config.progressInterval || 1e3,
      muted: muted || autoPlay,
      isLoaded: isLoaded || state.videoUrl.type === "READY" || state.videoUrl.type === "PROCESSING" || state.videoUrl.type === "ERROR",
      autoPlay,
      loop: playInLoop,
      isCustomVideo,
      poster,
      ...getLightProps(),
      ...getDownloadProps()
    }
  );
};
const normalizeVideoUrl = (url) => url ? url.toLowerCase().indexOf("vimeo") === 0 ? "https://" + url : url : void 0;
export {
  Video
};
//# sourceMappingURL=video.js.map
